import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { Button, Input } from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { ROUTE } from '~/app/routes';
import { Loading, RenderFields, SectionHeader } from '~/components';
import { StatusBadge } from '~/components/Table';
import { BankAccountSelector } from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { DocumentRepository, ReportingRepository, ReportingSettlementReport } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { EditToolbar, Inner } from '~/styles';
import { downloadFile, formatDateTime, formatString } from '~/util';
import {
  getFilenameForStatementReport,
  getReportPeriodLabel,
  getReportTypeLabel,
  getStatementPeriod,
  getStatementPeriodRange,
} from '~/util/reporting';

interface Params {
  id: string;
}

enum DocumentType {
  Statement = 'statement',
  Report = 'report',
}

const getDocumentType = (rawType: string): DocumentType => {
  if (rawType === 'bank_account_daily_statement') {
    return DocumentType.Statement;
  }
  if (rawType === 'bank_account_monthly_statement') {
    return DocumentType.Statement;
  }

  return DocumentType.Report;
};

const CopyRow: React.FC<{ value: string }> = ({ value }) => (
  <Input variant="copy" readOnly showCopyButton value={value} />
);

interface DownloadButtonProps {
  filename: string;
  label: string;
  id?: string;
}

export const DownloadButton: React.FC<DownloadButtonProps> = (props) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);

  const [running, setRunning] = useState<boolean>(false);

  const handleDownload = (docId?: string) => {
    if (!docId) {
      return;
    }

    setRunning(true);

    DocumentRepository.get(docId, { contentDisposition: `attachment; filename="${props.filename}"` })
      .then((response) => {
        downloadFile(response.url, props.filename);
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  return (
    <Button type="button" size="sm" onClick={() => handleDownload(props.id)} disabled={!props.id}>
      <Icon.AnimationDownload running={running} onDone={() => setRunning(false)} />
      {props.label}
    </Button>
  );
};

export const PageDocumentView: React.FC = () => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);

  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const [report, setReport] = useState<ReportingSettlementReport>();

  const documentType = report?.type ? getDocumentType(report.type) : undefined;
  const formMethods = useForm<ReportingSettlementReport>();

  const statementPeriod = getStatementPeriod(report?.type, report?.toDate)?.label;
  const periodLabel = statementPeriod ?? getReportPeriodLabel(report?.fromDate, report?.toDate);
  const statementPeriodRange = getStatementPeriodRange(
    report?.type,
    report?.fromDate,
    report?.toDate,
    report?.timeZone
  );
  const [showPeriodTimes, setShowPeriodTimes] = useState(false);
  const statementSubjectName = report?.statementSubject
    ? report.statementSubjectDescription || report.statementSubject
    : 'All Accounts';

  useBreadcrumbTitle(
    statementPeriod
      ? `${statementSubjectName} (${statementPeriod})`
      : periodLabel
        ? `${getReportTypeLabel(report?.type)} (${periodLabel})`
        : null
  );

  useEffect(() => {
    ReportingRepository.get(id)
      .then((response: any) => {
        setReport(response);
      })
      .catch((error: any) => {
        navigate(ROUTE.REPORTING);
        addDangerNotification({
          content: error.message,
        });
      });
  }, [id]);

  useEffect(() => {
    if (report) {
      formMethods.setValue('type', report.type || '-');
      formMethods.setValue('status', report.status || '-');
      formMethods.setValue('fromDate', report.fromDate || '-');
      formMethods.setValue('toDate', report.toDate || '-');
      formMethods.setValue('statementSubject', report.statementSubject || '');
      formMethods.setValue('initiatedAt', report.initiatedAt || '');
      formMethods.setValue('completedAt', report.completedAt || '');
    }
  }, [report]);

  return (
    <>
      <Loading isLoading={!documentType}>
        {documentType && (
          <>
            <SectionHeader text="Download" />
            <Inner pt={0} px="var(--page-margin)">
              <div className="flex flex-col gap-3 md:flex-row">
                {report?.pdfDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'pdf')}
                    label=".pdf File"
                    id={report?.pdfDocumentId}
                  />
                )}
                {report?.bai2DocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'bai2')}
                    label=".bai2 File"
                    id={report?.bai2DocumentId}
                  />
                )}
                {report?.csvDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'csv')}
                    label=".csv File"
                    id={report?.csvDocumentId}
                  />
                )}
                {report?.jsonDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'json')}
                    label=".json File"
                    id={report?.jsonDocumentId}
                  />
                )}
                {report?.parquetDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'parquet')}
                    label=".parquet File"
                    id={report?.parquetDocumentId}
                  />
                )}
              </div>
            </Inner>

            <FormProvider {...formMethods}>
              {RenderFields<ReportingSettlementReport>({
                sections: [
                  {
                    headline: 'Information',
                    fields: [
                      {
                        id: 'fromDate',
                        label: 'Period',
                        children: ({ value }) => {
                          if (periodLabel) {
                            return (
                              <div className="relative">
                                <Input
                                  variant="copy"
                                  readOnly
                                  showCopyButton
                                  className="[&_input]:pr-32"
                                  value={
                                    showPeriodTimes && statementPeriodRange
                                      ? `${formatDateTime(statementPeriodRange.start)} → ${formatDateTime(statementPeriodRange.end)}`
                                      : periodLabel
                                  }
                                />
                                {statementPeriodRange && (
                                  <Button
                                    type="button"
                                    variant="muted"
                                    size="xs"
                                    className="absolute top-1/2 right-10 -translate-y-1/2"
                                    onClick={() => setShowPeriodTimes((current) => !current)}
                                  >
                                    {showPeriodTimes ? 'Hide time' : 'Show time'}
                                  </Button>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-1">
                              {value || '-'}
                              <Icon.ArrowRight className="text-muted-foreground [--icon-size-x:16px] [--icon-size-y:16px]" />
                              {report?.toDate || '-'}
                            </div>
                          );
                        },
                      },
                      ...(report?.statementSubject
                        ? [
                            {
                              id: 'statementSubject' as keyof ReportingSettlementReport,
                              label: 'Subject',
                              children: ({ value }: { value: unknown }) => {
                                const subjectFallback = (
                                  <CopyRow value={report?.statementSubjectDescription || String(value) || '-'} />
                                );
                                if (String(value).startsWith('bacc_')) {
                                  return (
                                    <BankAccountSelector
                                      value={String(value)}
                                      onChange={() => undefined}
                                      onBlur={() => undefined}
                                      readOnly
                                      readOnlyFallback={subjectFallback}
                                      hideBalance
                                    />
                                  );
                                }
                                return subjectFallback;
                              },
                            },
                          ]
                        : []),
                      {
                        id: 'type',
                        label: 'Type',
                        children: ({ value }) => {
                          return value ? <StatusBadge>{getReportTypeLabel(String(value))}</StatusBadge> : <>-</>;
                        },
                      },
                      {
                        id: 'status',
                        label: 'Status',
                        children: ({ value }) => {
                          const status = String(value);
                          return value ? (
                            <StatusBadge
                              tone={status === 'completed' ? 'success' : status === 'failed' ? 'danger' : 'default'}
                            >
                              {formatString(status)}
                            </StatusBadge>
                          ) : (
                            <>-</>
                          );
                        },
                      },
                    ],
                  },
                  {
                    fields: [
                      {
                        id: 'initiatedAt',
                        label: 'Initiated At',
                        children: ({ value }) => {
                          return (
                            <CopyRow value={value ? formatDateTime(value as any, { timeZoneFormat: 'local' }) : '-'} />
                          );
                        },
                      },
                      {
                        id: 'completedAt',
                        label: 'Completed At',
                        children: ({ value }) => {
                          return (
                            <CopyRow value={value ? formatDateTime(value as any, { timeZoneFormat: 'local' }) : '-'} />
                          );
                        },
                      },
                    ],
                  },
                ],
              })}

              <Inner>
                <EditToolbar>
                  <Button type="button" onClick={() => navigate(-1)} variant="secondary" size="sm">
                    Back
                  </Button>
                </EditToolbar>
              </Inner>
            </FormProvider>
          </>
        )}
      </Loading>
    </>
  );
};
